import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/Primitives";
import Pagination, { usePagination } from "../components/Pagination";
import { Plus, X, UserPlus, Layers, Check, PanelLeft, Sparkles, Music2 } from "lucide-react";
import { toast } from "sonner";
import { useApiLoading, LoadingRow } from "../components/TableLoader";
import NavPermissionsModal from "../components/NavPermissionsModal";
import DV360MetricsModal from "../components/DV360MetricsModal";
import TikTokMetricsModal from "../components/TikTokMetricsModal";

const blankForm = () => ({
    name: "",
    slug: "",
    include_admin: true,
    admin_email: "",
    admin_password: "",
    admin_full_name: "",
    can_view_actual_cost: true,
    can_manage_branding: true,
    can_connect_integrations: true,
});

export default function Agencies() {
    const [list, setList] = useState([]);
    const apiLoading = useApiLoading();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(blankForm());
    const [busy, setBusy] = useState(false);
    const [metricsModal, setMetricsModal] = useState(null); // { agency, allowed, catalog }
    const [navModal, setNavModal] = useState(null); // { id, name } — opens NavPermissionsModal in scope="agency"
    const [dv360MetricsModal, setDv360MetricsModal] = useState(null); // { id, name } — DV360 metric access
    const [tiktokMetricsModal, setTiktokMetricsModal] = useState(null); // { id, name } — TikTok metric access

    const openMetricsModal = async (agency) => {
        try {
            const { data } = await api.get(`/detailed-reports/meta/allowed-metrics?agency_id=${agency.id}`);
            setMetricsModal({ agency, allowed: new Set(data.allowed_metrics), catalog: data.catalog });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const saveMetrics = async () => {
        if (!metricsModal) return;
        try {
            await api.put(`/detailed-reports/meta/allowed-metrics/${metricsModal.agency.id}`, {
                allowed_metrics: Array.from(metricsModal.allowed),
            });
            toast.success(`Metric access updated for ${metricsModal.agency.name}`);
            setMetricsModal(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const toggleMetricInModal = (key) => {
        if (!metricsModal) return;
        const next = new Set(metricsModal.allowed);
        if (next.has(key)) next.delete(key); else next.add(key);
        setMetricsModal({ ...metricsModal, allowed: next });
    };

    const load = async () => {
        const { data } = await api.get("/agencies");
        setList(data);
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { name: form.name, slug: form.slug };
            if (form.include_admin && form.admin_email && form.admin_password) {
                payload.admin_user = {
                    email: form.admin_email,
                    password: form.admin_password,
                    full_name: form.admin_full_name || form.admin_email.split("@")[0],
                    can_view_actual_cost: form.can_view_actual_cost,
                    can_manage_branding: form.can_manage_branding,
                    can_connect_integrations: form.can_connect_integrations,
                };
            }
            await api.post("/agencies", payload);
            toast.success(form.include_admin ? "Agency + admin user created" : "Agency created");
            setOpen(false);
            setForm(blankForm());
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const pager = usePagination(list, 25);

    return (
        <div data-testid="agencies-page">
            <PageHeader
                eyebrow="Tenant management"
                title="Agencies"
                subtitle="Each agency is an isolated white-label tenant with its own branding, domains, users, and integrations."
                actions={
                    <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="agency-create-btn">
                        <Plus className="h-4 w-4" /> New Agency
                    </button>
                }
            />

            <div className="bg-white border border-zinc-200">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-zinc-100 border-b border-zinc-200">
                            <th className="overline text-left p-3">Name</th>
                            <th className="overline text-left p-3">Slug</th>
                            <th className="overline text-left p-3">Status</th>
                            <th className="overline text-left p-3">Created</th>
                            <th className="overline text-right p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pager.slice.map((a) => (
                            <tr key={a.id} className="border-b border-zinc-200 last:border-0">
                                <td className="p-3 text-zinc-900 font-medium">{a.name}</td>
                                <td className="p-3 text-zinc-500 font-mono text-xs">{a.slug}</td>
                                <td className="p-3">
                                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${a.is_active ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-zinc-200 text-zinc-500"}`}>
                                        {a.is_active ? "active" : "inactive"}
                                    </span>
                                </td>
                                <td className="p-3 text-zinc-500 text-xs">{new Date(a.created_at).toLocaleDateString()}</td>
                                <td className="p-3 text-right">
                                    <div className="inline-flex items-center gap-3 flex-wrap justify-end">
                                        <button
                                            onClick={() => openMetricsModal(a)}
                                            className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:wl-accent-text"
                                            data-testid={`agency-metrics-btn-${a.id}`}
                                            title="Choose which Meta detailed-report metrics this agency can see"
                                        >
                                            <Layers className="h-3.5 w-3.5" /> Meta metrics
                                        </button>
                                        <button
                                            onClick={() => setDv360MetricsModal({ id: a.id, name: a.name })}
                                            className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:wl-accent-text"
                                            data-testid={`agency-dv360-metrics-btn-${a.id}`}
                                            title="Choose which DV360 detailed-report metrics this agency can see"
                                        >
                                            <Sparkles className="h-3.5 w-3.5" /> DV360 metrics
                                        </button>
                                        <button
                                            onClick={() => setTiktokMetricsModal({ id: a.id, name: a.name })}
                                            className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:wl-accent-text"
                                            data-testid={`agency-tiktok-metrics-btn-${a.id}`}
                                            title="Choose which TikTok detailed-report metrics this agency can see"
                                        >
                                            <Music2 className="h-3.5 w-3.5" style={{ color: "#FE2C55" }} /> TikTok metrics
                                        </button>
                                        <button
                                            onClick={() => setNavModal({ id: a.id, name: a.name })}
                                            className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:wl-accent-text"
                                            data-testid={`agency-nav-btn-${a.id}`}
                                            title="Choose which sidebar items this agency's admins can see"
                                        >
                                            <PanelLeft className="h-3.5 w-3.5" /> Sidebar access
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!list.length && (
                            apiLoading ? <LoadingRow colSpan={5} /> :
                            <tr><td colSpan={5} className="p-8 text-center text-zinc-500">No agencies yet.</td></tr>
                        )}
                    </tbody>
                </table>
                {list.length > 0 && (
                    <Pagination
                        page={pager.page}
                        totalPages={pager.totalPages}
                        pageSize={pager.pageSize}
                        range={pager.range}
                        setPage={pager.setPage}
                        setPageSize={pager.setPageSize}
                        dataTestId="agencies-pagination"
                    />
                )}
            </div>

            {open && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={submit}
                        className="bg-zinc-50 border border-zinc-200 w-full max-w-xl p-6 rounded-md my-8"
                        data-testid="agency-create-modal"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-xl">New Agency</h3>
                            <button type="button" onClick={() => setOpen(false)}><X className="h-4 w-4 text-zinc-500" /></button>
                        </div>

                        <div className="space-y-3 mb-5">
                            <div className="overline">Workspace</div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="overline">Name</label>
                                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="agency-name-input" />
                                </div>
                                <div>
                                    <label className="overline">Slug</label>
                                    <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} required className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded font-mono" data-testid="agency-slug-input" />
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-zinc-200 pt-4">
                            <label className="flex items-center gap-2 text-sm text-zinc-800 cursor-pointer mb-3">
                                <input type="checkbox" checked={form.include_admin} onChange={(e) => setForm({ ...form, include_admin: e.target.checked })} data-testid="agency-include-admin" />
                                <UserPlus className="h-4 w-4 wl-accent-text" />
                                Create initial admin user
                            </label>
                            {form.include_admin && (
                                <div className="space-y-3 pl-6">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="overline">Full name</label>
                                            <input required={form.include_admin} value={form.admin_full_name} onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="agency-admin-name-input" placeholder="Maya Iyer" />
                                        </div>
                                        <div>
                                            <label className="overline">Email</label>
                                            <input required={form.include_admin} type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="agency-admin-email-input" placeholder="admin@agency.com" />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="overline">Password</label>
                                            <input required={form.include_admin} type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="agency-admin-password-input" placeholder="at least 8 characters" minLength={6} />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3 bg-zinc-50 border border-zinc-200 rounded p-3">
                                        {[
                                            { k: "can_view_actual_cost", label: "View campaign spends & margin" },
                                            { k: "can_manage_branding", label: "Manage branding" },
                                            { k: "can_connect_integrations", label: "Connect integrations" },
                                        ].map((p) => (
                                            <label key={p.k} className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer">
                                                <input type="checkbox" checked={form[p.k]} onChange={(e) => setForm({ ...form, [p.k]: e.target.checked })} className="mt-0.5" data-testid={`agency-perm-${p.k}`} />
                                                <span>{p.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-900">Cancel</button>
                            <button type="submit" disabled={busy} className="px-4 py-2 rounded text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="agency-submit-btn">
                                {busy ? "Creating…" : "Create agency"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {metricsModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setMetricsModal(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white border border-zinc-200 rounded-md w-full max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="metrics-access-modal">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
                            <div>
                                <div className="overline">Detailed-report metric access</div>
                                <h3 className="font-display text-lg text-zinc-900">{metricsModal.agency.name}</h3>
                                <p className="text-xs text-zinc-500 mt-1">Pick which metrics this agency&apos;s admins and advertisers can see in Detailed Reports. Blocked metrics are zeroed out in API responses + hidden in the metric picker.</p>
                            </div>
                            <button onClick={() => setMetricsModal(null)} className="text-zinc-400 hover:text-zinc-900"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="flex items-center justify-between px-5 py-2 border-b border-zinc-100 text-xs">
                            <span className="text-zinc-500">{metricsModal.allowed.size} of {metricsModal.catalog.length} metrics enabled</span>
                            <div className="flex items-center gap-3">
                                <button onClick={() => setMetricsModal({ ...metricsModal, allowed: new Set(metricsModal.catalog) })} className="wl-accent-text hover:underline" data-testid="metrics-modal-select-all">Select all</button>
                                <span className="text-zinc-300">·</span>
                                <button onClick={() => setMetricsModal({ ...metricsModal, allowed: new Set() })} className="text-zinc-500 hover:text-zinc-900" data-testid="metrics-modal-clear">Clear</button>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 px-5 py-4">
                            {metricsModal.catalog.map((key) => {
                                const checked = metricsModal.allowed.has(key);
                                return (
                                    <label key={key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 rounded cursor-pointer text-sm" data-testid={`metric-access-${key}`}>
                                        <span className={`h-4 w-4 rounded border flex items-center justify-center ${checked ? "wl-accent-bg wl-accent-border" : "border-zinc-300 bg-white"}`}>
                                            {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                        </span>
                                        <input type="checkbox" checked={checked} onChange={() => toggleMetricInModal(key)} className="sr-only" />
                                        <span className="text-zinc-700 capitalize">{key.replace(/_/g, " ")}</span>
                                    </label>
                                );
                            })}
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-4 border-t border-zinc-200">
                            <button type="button" onClick={() => setMetricsModal(null)} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-900">Cancel</button>
                            <button type="button" onClick={saveMetrics} className="px-4 py-2 rounded text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="metrics-access-save">Save changes</button>
                        </div>
                    </div>
                </div>
            )}

            {navModal && (
                <NavPermissionsModal
                    scope="agency"
                    id={navModal.id}
                    name={navModal.name}
                    onClose={() => setNavModal(null)}
                />
            )}

            {dv360MetricsModal && (
                <DV360MetricsModal
                    scope="agency"
                    id={dv360MetricsModal.id}
                    name={dv360MetricsModal.name}
                    onClose={() => setDv360MetricsModal(null)}
                />
            )}

            {tiktokMetricsModal && (
                <TikTokMetricsModal
                    scope="agency"
                    id={tiktokMetricsModal.id}
                    name={tiktokMetricsModal.name}
                    onClose={() => setTiktokMetricsModal(null)}
                />
            )}
        </div>
    );
}
